import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, firstValueFrom, Observable, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductCategories } from '../_enums/product-categories';
import { GET_PRODUCT_BY_HANDLE, GET_PRODUCT_BY_ID, GET_PRODUCT_RECOMMENDATIONS } from '../_graphql/products';
import { IProductType, IShopifyCart, IShopifyImage, IShopifyProduct, IShopifyProductVariant, IShopifySeo, IVendor } from '@app/_shared/_models';
import { getSellingPlanAllocationsFromVariantNode } from '../_helpers/subscription-helpers';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { CartService } from './cart.service';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  private readonly apolloService = inject(Apollo);
  private readonly client = inject(HttpClient);
  private readonly cartService = inject(CartService);
  private cart: IShopifyCart | undefined;

  public product$!: Observable<IShopifyProduct | undefined>;

  private readonly _product: BehaviorSubject<IShopifyProduct | undefined>;

  constructor() {
    this._product = new BehaviorSubject<IShopifyProduct | undefined>(undefined);
    this.product$ = this._product.asObservable();
    this.cartService.cart$.pipe(takeUntilDestroyed())
      .subscribe((cart: IShopifyCart | undefined) => {
        if (cart) {
          this.cart = cart;
        }
    });
  }

  //#region Action Methods
  public async loadAllProducts(): Promise<Array<IShopifyProduct>> {
    const productResponse = await firstValueFrom(this.client.get<Array<IShopifyProduct>>(`${environment.syncApiUrl}/sync/product`));

    return productResponse;
  }

  public loadProductById(id: string): void {
    this.getProductById(id)
      .pipe(take(1))
      .subscribe((result: any) => {
        const product = this.getProductFromQuery(result);
        this._product.next(product);
      });
  }

  public async loadProductByHandle(handle: string): Promise<IShopifyProduct | undefined> {
    const productResponse = await firstValueFrom(this.getProductByHandle(handle));
    const product = this.getProductFromQuery(productResponse);
    this._product.next(product);

    return product;
  }

  public unloadProduct(): void {
    this._product.next(undefined);
  }

  public async loadSimilarProducts(productId: string): Promise<Array<IShopifyProduct>> {
    const similarProductsResponse = await firstValueFrom(this.getSimilarProducts(productId));
    const similarProducts = new Array<IShopifyProduct>();

    similarProductsResponse.data?.productRecommendations?.forEach((product: any) => {
      const similarProduct = this.getProductFromQuery({ data: { product: product } });
      if (similarProduct) {
        similarProducts.push(similarProduct);
      }
    });

    return similarProducts;
  }
  //#endregion

  public static sortProducts(products: Array<IShopifyProduct>, sortOption: string, keyword?: string): Array<IShopifyProduct> {
    switch (sortOption) {
      case 'Alphabetically (A-Z)':
        return products.sort((a, b) => a.title.localeCompare(b.title));
      case 'Alphabetically (Z-A)':
        return products.sort((a, b) => b.title.localeCompare(a.title));
      case 'Price (Low to High)':
        return products.sort((a, b) => a.price - b.price);
      case 'Price (High to Low)':
        return products.sort((a, b) => b.price - a.price);
      case 'Most Relevant':
        if (keyword) {
          let keywords = keyword.split(/(\s+)/);
          keywords = keywords.filter(function(str) {
            return /\S/.test(str);
          });

          return products.sort((a, b) => {
            if (keywords.some(k => a.title.toLowerCase().includes(k))) {
              return -1;
            }
            if (keywords.some(k => b.title.toLowerCase().includes(k))) {
              return 1;
            }

            return 0;
          });
        } else {
          return products;
        }
      default:
        return products;
    }
  }

  public static filterByVendors(products: Array<IShopifyProduct>, vendors: Array<IVendor>): Array<IShopifyProduct> {
    const brands = vendors?.map(v => v.name);
    if (!brands || brands?.length < 1) {
      return products;
    } else {
      const filteredProducts = products.filter(p => brands.includes(p.vendor));

      return filteredProducts;
    }
  }

  public static filterByProductType(products: Array<IShopifyProduct>, productTypes: Array<IProductType>): Array<IShopifyProduct> {
    const tags = productTypes?.flatMap(p => p.tag) ?? undefined;
    if (!tags || tags?.length < 1) {
      return products;
    } else {
      const filteredProducts = new Array<IShopifyProduct>();
      products.forEach((product: IShopifyProduct) => {
        if (product.tags.length > 0) {
          tags.forEach((tag: string) => {
            if (product.tags.includes(tag)) {
              filteredProducts.push(product);
            }
          })
        }
      });

      return filteredProducts;
    }
  }

  private getProductById(id: string): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_PRODUCT_BY_ID,
        variables: {
          id
        }
      })
      .valueChanges.pipe(take(1));
  }

  private getProductByHandle(handle: string): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_PRODUCT_BY_HANDLE,
        variables: {
          handle
        }
      })
      .valueChanges.pipe(take(1));
  }

  private getSimilarProducts(productId: string): Observable<any> {
    return this.apolloService
      .watchQuery({
        query: GET_PRODUCT_RECOMMENDATIONS,
        variables: {
          productId
        }
      })
      .valueChanges.pipe(take(1));
  }

  private getProductFromQuery(result: any): IShopifyProduct | undefined {
    const productResponse = result.data?.product;
    if (productResponse) {
      const image: IShopifyImage = {
        altText: productResponse.featuredImage.altText,
        url: productResponse.featuredImage.url,
        width: productResponse.featuredImage.width,
        height: productResponse.featuredImage.height,
        id: productResponse.featuredImage.id
      }

      let category = ProductCategories.BOTH;

      if (productResponse.tags?.length > 0) {
        if (productResponse.tags?.some((c: string) => c.includes('Pool'))
              && (productResponse.tags.some((c: string) => c.includes('Hot Tub')) || productResponse.tags.some((c: string) => c.includes('Spas')))) {
          category = ProductCategories.BOTH;
        } else if (productResponse.tags.some((c: string) => c.includes('Pool'))) {
          category = ProductCategories.POOL;
        } else if (productResponse.tags.some((c: string) => c.includes('Hot Tub')) || productResponse.tags.some((c: string) => c.includes('Spas'))) {
          category = ProductCategories.SPA;
        }
      }

      let variants = new Array<IShopifyProductVariant>();
      productResponse.variants?.edges?.forEach((variantEdge: any) => {
        const variantNode = variantEdge?.node;

        const variantImage: IShopifyImage = {
          altText: variantNode.image.altText,
          url: variantNode.image.url,
          width: variantNode.image.width,
          height: variantNode.image.height,
          id: variantNode.image.id
        };

        // Get Subcription Plan
        const sellingPlanAllocations = getSellingPlanAllocationsFromVariantNode(variantNode);

        const amountInCart = this.cart?.lines?.find((line: any) => line.variant.id === variantNode.id)?.quantity ?? 0;

        const variant: IShopifyProductVariant = {
          availableForSale: variantNode?.availableForSale,
          amountInCart,
          id: variantNode.id,
          image: variantImage,
          inventory: variantNode.quantityAvailable,
          sku: variantNode.sku,
          variantTitle: variantNode.title,
          productTitle: productResponse.title,
          price: variantNode.price?.amount != null ? +variantNode.price.amount : 0,
          weight: variantNode.weight,
          weightUnit: variantNode.weightUnit,
          sellingPlanAllocations
        };

        variants.push(variant);
      });

      const sellingPlanGroups = productResponse.sellingPlanGroups?.edges?.map((groupEdge: any) => {
        const groupNode = groupEdge?.node;
        const sellingPlans = groupNode?.sellingPlans?.edges?.map((sellingPlan: any) => {
          const planNode = sellingPlan?.node;

          return {
            id: planNode.id,
            name: planNode.name,
            priceAdjustments: planNode.priceAdjustments?.map((priceAdjustment: any) => {
              return {
                orderCount: priceAdjustment?.orderCount,
                adjustmentValue: {
                  adjustmentAmount: priceAdjustment?.adjustmentValue?.adjustmentAmount?.amount,
                  price: priceAdjustment?.adjustmentValue?.price?.amount,
                  adjustmentPercentage: priceAdjustment?.adjustmentValue?.adjustmentPercentage
                }
              }
            }),
            description: planNode.description,
            recurringDeliveries: planNode.recurringDeliveries
          }
        });

        return {
          name: groupNode.name,
          options: groupNode.options,
          sellingPlans
        }
      });

      const seo: IShopifySeo = {
        description: productResponse.seo?.description ?? '',
        title: productResponse.seo?.title ?? ''
      }

      const product: IShopifyProduct = {
        descriptionHtml: productResponse.descriptionHtml ?? '',
        id: productResponse.id,
        handle: productResponse.handle,
        image,
        inventory: productResponse.totalInventory ?? 0,
        price: variants[0]?.price ?? 0,
        tags: productResponse.tags,
        title: productResponse.title ?? '',
        vendor: productResponse.vendor ?? '',
        category,
        variants,
        seo,
        sellingPlanGroups
      };

      return product;
    }

    return undefined;
  }

  // Good Example of Dealing with Observables
  // public static sortProducts(products$: Observable<Array<IShopifyProduct>>, sortOption: string): Observable<Array<IShopifyProduct>> {
  //   switch (sortOption) {
  //     case 'Alphabetically (A-Z)':
  //       return products$.pipe(take(1), map(products => products.sort((a, b) => a.title.localeCompare(b.title))));
  //     case 'Alphabetically (Z-A)':
  //       return products$.pipe(take(1), map(products => products.sort((a, b) => b.title.localeCompare(a.title))));
  //     case 'Price (Low to High)':
  //       return products$.pipe(take(1), map(products => products.sort((a, b) => a.price - b.price)));
  //     case 'Price (High to Low)':
  //       return products$.pipe(take(1), map(products => products.sort((a, b) => b.price - a.price)));
  //     default:
  //       return products$;
  //   }
}
